// HowItWorks — 3-step process section | directly after hero | scroll-reveal stagger

import { useRef } from 'react'
import {
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
  useMotionValue,
  type MotionValue,
} from 'framer-motion'
import { useWindowWidth } from '../../hooks/useWindowWidth'
import { CALENDLY_URL } from '../../constants'

const EASE = [0.22, 1, 0.36, 1] as const;

const STEPS = [
  {
    number: '1',
    heading: 'Start with a call',
    body: 'Tell us who you are — a firm, a school, or a student — and what you need. No account required.',
  },
  {
    number: '2',
    heading: 'Set up your workspace',
    body: 'Students, counselors, and leadership get one shared workspace, connected from day one.',
  },
  {
    number: '3',
    heading: 'Work in one place',
    body: 'Essays, deadlines, college lists, progress — everyone sees what matters, with AI help at every step.',
  },
];

// ─── StepNode — numbered marker that lights as the drawn line reaches it ──────

interface StepNodeProps {
  number: string
  index: number
  progress: MotionValue<number>
  prefersReduced: boolean
  isMobile: boolean
}

function StepNode({ number, index, progress, prefersReduced, isMobile }: StepNodeProps) {
  // Node i activates once the line has drawn past its position along the track
  const threshold = index / (STEPS.length - 1 || 1)
  const lit = useTransform(progress, (p): number => (p >= threshold - 0.02 ? 1 : 0))

  const bg = useTransform(lit, [0, 1], ['rgba(139,92,246,0.10)', 'rgba(139,92,246,0.30)'])
  const border = useTransform(lit, [0, 1], ['rgba(139,92,246,0.25)', 'rgba(167,139,250,0.95)'])
  const glow = useTransform(lit, [0, 1], ['0 0 0px rgba(139,92,246,0)', '0 0 18px rgba(139,92,246,0.55)'])
  const color = useTransform(lit, [0, 1], ['rgba(167,139,250,0.55)', '#EDE9FE'])

  // Reduced motion: fully lit, no scroll binding
  const staticStyle = {
    background: 'rgba(139,92,246,0.30)',
    border: '1px solid rgba(167,139,250,0.95)',
    boxShadow: '0 0 18px rgba(139,92,246,0.55)',
    color: '#EDE9FE',
  }

  return (
    <motion.div
      style={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Cormorant Garamond, serif',
        fontSize: 18,
        fontWeight: 700,
        flexShrink: 0,
        position: 'relative',
        zIndex: 1,
        // Sits centered on the rail
        marginLeft: isMobile ? -44 : 0,
        ...(prefersReduced
          ? staticStyle
          : {
              background: bg,
              border: '1px solid transparent',
              borderColor: border,
              boxShadow: glow,
              color,
            }),
      }}
    >
      {number}
    </motion.div>
  )
}

function HowItWorks() {
  const width = useWindowWidth()
  const isMobile = width < 768
  const prefersReduced = useReducedMotion()

  // Journey line draws as the steps block crosses the viewport
  const journeyRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: journeyRef,
    offset: ['start 85%', 'center 55%'],
  })
  // Reduced motion: fully drawn, no scroll binding
  const scrollLine = useSpring(scrollYProgress, { stiffness: 120, damping: 28, mass: 0.4 })
  const staticLine = useMotionValue(1)
  const lineProgress = prefersReduced ? staticLine : scrollLine

  return (
    <section
      style={{
        position: 'relative',
        zIndex: 1,
        // Generous top padding gives the eyebrow clear space below the hero's
        // ticker strip, matching the portal section's vertical rhythm
        padding: isMobile ? '96px 24px 72px' : '140px 24px 110px',
        background: 'transparent',
      }}
    >
      {/* Top separator */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 1,
          background: 'linear-gradient(to right, transparent, rgba(109,40,217,0.3), transparent)',
          pointerEvents: 'none',
        }}
        aria-hidden="true"
      />

      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        {/* Header */}
        <motion.div
          style={{ textAlign: 'center', marginBottom: isMobile ? 48 : 64 }}
          initial={prefersReduced ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <span
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.15em',
              textTransform: 'uppercase' as const,
              color: 'rgba(139,92,246,0.8)',
              display: 'block',
              marginBottom: 16,
            }}
          >
            How it works
          </span>
          <h2
            style={{
              fontFamily: 'Cormorant Garamond, serif',
              fontSize: isMobile ? 'clamp(28px, 7vw, 44px)' : 52,
              fontWeight: 700,
              color: '#FFFFFF',
              lineHeight: 1.1,
              margin: 0,
            }}
          >
            Up and running in three steps.
          </h2>
        </motion.div>

        {/* Connected journey — a track line spans the three steps and draws
            itself on scroll; nodes light in sequence as the line reaches them.
            Horizontal on desktop, vertical down the left edge on mobile. */}
        <div
          ref={journeyRef}
          style={{
            position: 'relative',
            marginBottom: isMobile ? 48 : 64,
            // Room for the vertical rail on mobile
            paddingLeft: isMobile ? 44 : 0,
          }}
        >
          {/* Track — resting rail */}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              background: 'rgba(139,92,246,0.14)',
              ...(isMobile
                ? { left: 17, top: 18, bottom: 18, width: 2 }
                : { left: '16.6%', right: '16.6%', top: 18, height: 2 }),
            }}
          />

          {/* Track — drawn portion, bound to scroll progress */}
          <motion.div
            aria-hidden="true"
            style={{
              position: 'absolute',
              background: 'linear-gradient(90deg, rgba(139,92,246,0.9), rgba(167,139,250,0.9))',
              transformOrigin: isMobile ? 'top' : 'left',
              ...(isMobile
                ? { left: 17, top: 18, bottom: 18, width: 2, scaleY: lineProgress }
                : { left: '16.6%', right: '16.6%', top: 18, height: 2, scaleX: lineProgress }),
            }}
          />

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: isMobile ? 36 : 32,
              position: 'relative',
            }}
          >
            {STEPS.map((step, i) => (
              <motion.div
                key={step.number}
                style={{
                  display: 'flex',
                  flexDirection: 'column' as const,
                  gap: 14,
                  alignItems: isMobile ? 'flex-start' : 'center',
                  textAlign: isMobile ? 'left' : 'center',
                }}
                initial={prefersReduced ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.55, ease: EASE, delay: prefersReduced ? 0 : i * 0.14 }}
              >
                <StepNode
                  number={step.number}
                  index={i}
                  progress={lineProgress}
                  prefersReduced={!!prefersReduced}
                  isMobile={isMobile}
                />
                <h3
                  style={{
                    fontFamily: 'Cormorant Garamond, serif',
                    fontSize: 22,
                    fontWeight: 700,
                    color: '#FFFFFF',
                    lineHeight: 1.2,
                    margin: 0,
                  }}
                >
                  {step.heading}
                </h3>
                <p
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 14,
                    color: 'rgba(255,255,255,0.55)',
                    lineHeight: 1.65,
                    margin: 0,
                    maxWidth: isMobile ? undefined : 280,
                  }}
                >
                  {step.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTAs — institution primary, student secondary */}
        <motion.div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isMobile ? 16 : 24,
          }}
          initial={prefersReduced ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6, ease: EASE, delay: prefersReduced ? 0 : 0.36 }}
        >
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="landing-cta"
            style={{
              padding: isMobile ? '13px 28px' : '15px 36px',
              fontSize: isMobile ? '14px' : '15px',
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            Book a Demo →
          </a>

          {/* Secondary — student path */}
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="landing-secondary"
          >
            Book a Free Intro Call →
          </a>
        </motion.div>
      </div>
    </section>
  )
}

export default HowItWorks
